import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import {
  selectConfigOpen,
  updateConfigOpen,
  updateMarket,
  updateRegressionTestId,
  updateRegressionModalOpen,
} from '../../redux/app/configSlice';
import {
  getRegressionTestIdsAsync,
  selectRegressionTestIds,
  selectRegressionTestIdsLoading,
} from '../../redux/regressionTestIds/getRegressionTestIdsSlice';
import { getMarketsAsync, selectMarkets } from '../../redux/markets/getMarketsSlice';
import { useSelector, useDispatch } from 'react-redux';
import SettingsIcon from '@mui/icons-material/Settings';
import { FLAG_MAP, PLACEHOLDER_FLAG } from '../../config/marketFlags';

// Styles for modal box
const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 500,
  bgcolor: 'background.paper',
  borderRadius: 4,
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
  p: 4,
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
};

export default function ConfigModal() {
  const dispatch = useDispatch();
  const [market, setMarket] = React.useState(
    localStorage.getItem('market') === null
      ? { id: '', name: '' }
      : JSON.parse(localStorage.getItem('market')),
  );
  const [regressionTestId, setRegressionTestId] = React.useState(
    localStorage.getItem('regressionTestId') || null,
  );
  const modalOpen = useSelector(selectConfigOpen);
  const regressionTestIds = useSelector(selectRegressionTestIds);
  const loadingIds = useSelector(selectRegressionTestIdsLoading);
  const markets = useSelector(selectMarkets);

  React.useEffect(() => {
    if (modalOpen) {
      dispatch(getRegressionTestIdsAsync());
      dispatch(getMarketsAsync());
    }
  }, [modalOpen, dispatch]);

  const handleMarketChange = (event) => {
    const selectedId = event.target.value;
    const found = markets.find((m) => m.id === selectedId);
    if (found) {
      setMarket({ id: found.id, name: found.name });
    }
  };

  const localStorageSetter = (market, regressionTestId) => {
    localStorage.setItem('market', JSON.stringify(market));
    localStorage.setItem('regressionTestId', regressionTestId);
  };

  // Check if cancel should be disabled (market missing from localStorage)
  const canCancel = localStorage.getItem('market') !== null;

  return (
    <div>
      <Modal
        open={modalOpen}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" fontWeight="bold">
              Configuration
            </Typography>
          </Box>

          {/* Description */}
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Please configure your market before continuing. Regression test ID is optional.
          </Typography>

          {/* Market Selection Dropdown */}
          <FormControl
            fullWidth
            sx={{
              '& .MuiInputLabel-root.Mui-focused': {
                color: 'primary.main',
              },
            }}
          >
            <InputLabel id="market-select-label">Market</InputLabel>
            <Select
              labelId="market-select-label"
              id="market-select"
              value={market.id || ''}
              label="Market"
              onChange={handleMarketChange}
              sx={{
                bgcolor: 'custom.surfaceAlt',
                borderRadius: 3,
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'text.secondary',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                  },
                },
              }}
            >
              {markets.map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <img
                      src={FLAG_MAP[m.id] || PLACEHOLDER_FLAG}
                      alt={`${m.name} flag`}
                      style={{ width: 30, height: 30, marginRight: 8, mixBlendMode: 'multiply' }}
                    />
                    {m.name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Regression Test ID Input */}
          <Autocomplete
            fullWidth
            options={regressionTestIds}
            value={regressionTestId}
            onChange={(event, newValue) => setRegressionTestId(newValue)}
            loading={loadingIds}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Regression Test ID"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingIds ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'custom.surfaceAlt',
                    borderRadius: 3,
                    '&:hover fieldset': {
                      borderColor: 'text.secondary',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: 'primary.main',
                  },
                }}
              />
            )}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button
              startIcon={<SettingsIcon />}
              onClick={() => dispatch(updateRegressionModalOpen(true))}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                color: 'primary.main',
                px: 1,
                '&:hover': {
                  backgroundColor: 'transparent',
                  textDecoration: 'underline',
                },
              }}
            >
              Manage Regression IDs
            </Button>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button
              disabled={!canCancel}
              variant="outlined"
              sx={{
                borderColor: 'divider',
                color: 'text.secondary',
                textTransform: 'none',
                fontWeight: 'bold',
                '&:hover': {
                  borderColor: 'text.secondary',
                  backgroundColor: 'action.hover',
                },
              }}
              onClick={() => {
                // Reset to stored values on cancel
                const storedMarket = localStorage.getItem('market');
                const storedRegressionTestId = localStorage.getItem('regressionTestId');
                if (storedMarket) {
                  setMarket(JSON.parse(storedMarket));
                }
                if (storedRegressionTestId) {
                  setRegressionTestId(storedRegressionTestId);
                }
                dispatch(updateConfigOpen(false));
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={!market.id}
              sx={{
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                textTransform: 'none',
                fontWeight: 'bold',
                '&:hover': { bgcolor: 'primary.light' },
              }}
              onClick={() => {
                localStorageSetter(market, regressionTestId);
                dispatch(updateMarket(market));
                dispatch(updateRegressionTestId(regressionTestId));
                dispatch(updateConfigOpen(false));
                // Dispatch custom events for same-window listeners
                window.dispatchEvent(new Event('marketChanged'));
                window.dispatchEvent(new Event('configChanged'));
              }}
            >
              Save changes
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
}
